// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createDataProvider } from '@shared/ui-kit/providers/data-provider/create-data-provider';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo, useEffect } from 'react';
import type {
  SourceAsset,
  BuyOperationData,
} from '@shared/portfolio-operations/types';
import {
  buyOperationFormSchema,
  type BuyOperationFormData,
} from '@shared/portfolio-operations/schemas/buy-operation';
import { getCurrencySymbol } from '@shared/portfolio-operations/utils/currency';

export interface BuyOperationProviderProps {
  sourceAsset: SourceAsset;
  onFormDataChange?: (data: BuyOperationData) => void;
  onFormValidationChange?: (isValid: boolean) => void;
}

interface BuyOperationContextValue {
  sourceAsset: SourceAsset;
  form: ReturnType<typeof useForm<BuyOperationFormData>>;
  currencyPrefix: string;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  formData: BuyOperationData;
  amountOnly: boolean;
}

const useBuyOperationFactory = ({
  sourceAsset,
  onFormDataChange,
  onFormValidationChange,
}: BuyOperationProviderProps): BuyOperationContextValue => {
  const form = useForm<BuyOperationFormData>({
    resolver: zodResolver(buyOperationFormSchema),
    mode: 'onChange',
    defaultValues: { amount: 0, shares: 0, inputType: 'amount' },
  });

  const amount = form.watch('amount');
  const shares = form.watch('shares');

  const pricePerShare = useMemo(
    () =>
      sourceAsset.quantity > 0 ? sourceAsset.investment / sourceAsset.quantity : 0,
    [sourceAsset.investment, sourceAsset.quantity],
  );

  const amountOnly = useMemo(
    () => sourceAsset.quantity === 0 || pricePerShare === 0,
    [sourceAsset.quantity, pricePerShare],
  );

  const currencyPrefix = useMemo(
    () => getCurrencySymbol(sourceAsset.currency),
    [sourceAsset.currency],
  );

  const inputType = form.watch('inputType');

  const formData: BuyOperationData = useMemo(
    () => ({
      amount,
      shares: amountOnly ? 0 : shares,
      inputType: amountOnly ? 'amount' : inputType,
    }),
    [amount, amountOnly, inputType, shares],
  );

  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const name = e.target.name as 'amount' | 'shares';
      const opts = { shouldValidate: true as const };

      switch (name) {
        case 'amount': {
          const val = form.getValues('amount');
          const shares =
            pricePerShare > 0 ? Math.round((val / pricePerShare) * 100) / 100 : 0;
          form.setValue('shares', shares, opts);
          form.setValue('inputType', 'amount', opts);
          break;
        }
        case 'shares': {
          const val = form.getValues('shares');
          const clamped = Math.max(val, 0);
          const amount =
            pricePerShare > 0 ? Math.round(clamped * pricePerShare * 100) / 100 : 0;
          form.setValue('amount', amount, opts);
          form.setValue('inputType', 'shares', opts);
          break;
        }
        default:
          return;
      }
    },
    [form, pricePerShare],
  );

  useEffect(() => {
    onFormDataChange?.(formData);
  }, [formData, onFormDataChange]);

  useEffect(() => {
    onFormValidationChange?.(amount > 0);
  }, [amount, onFormValidationChange]);

  return {
    sourceAsset,
    form,
    currencyPrefix,
    onBlur,
    formData,
    amountOnly,
  };
};

export const { BuyOperationProvider, useBuyOperationData } = createDataProvider({
  name: 'BuyOperation',
  factory: useBuyOperationFactory,
});
